import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { debounce } from 'lodash';
import { useAuth } from '../../contexts/AuthContext';
import useIndexSearch from '../../hooks/useIndexSearch';

// Function to play a delightful, pleasant bubble notification sound
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Master gain for overall volume control
    const masterGain = audioContext.createGain();
    masterGain.gain.setValueAtTime(0.35, audioContext.currentTime);
    masterGain.connect(audioContext.destination);
    
    // Create three musical notes in a pleasant, uplifting pattern
    // Using a major triad (G4-B4-D5) which sounds cheerful and resolved
    const notes = [
      { note: 392.00, time: 0.00, duration: 0.12 },  // G4
      { note: 493.88, time: 0.07, duration: 0.15 },  // B4
      { note: 587.33, time: 0.16, duration: 0.18 }   // D5
    ];
    
    // Create each bubble note with its own character
    notes.forEach((note, index) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      // Use sine wave for purity of tone
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(note.note, audioContext.currentTime + note.time);
      
      // Each bubble has a slightly different envelope
      // First is quick, middle is moderate, last is slightly longer
      gainNode.gain.setValueAtTime(0, audioContext.currentTime + note.time);
      gainNode.gain.linearRampToValueAtTime(0.25 + (index * 0.03), audioContext.currentTime + note.time + 0.02);
      
      // Longer decay for the final note to give a sense of completion
      const decayTime = index === 2 ? 0.16 : 0.10;
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + note.time + decayTime);
      
      // Add a tiny pitch rise at the start of each note for a "pop" quality
      oscillator.frequency.linearRampToValueAtTime(
        note.note * 1.02, 
        audioContext.currentTime + note.time + 0.01
      );
      oscillator.frequency.linearRampToValueAtTime(
        note.note, 
        audioContext.currentTime + note.time + 0.03
      );
      
      // Connect and schedule
      oscillator.connect(gainNode);
      gainNode.connect(masterGain);
      
      oscillator.start(audioContext.currentTime + note.time);
      oscillator.stop(audioContext.currentTime + note.time + note.duration);
    });
    
  } catch (error) {
  }
}

const AutoCompleteSearch = ({
  onSelect,
  inputValue,
  cursorPosition,
  minChars = 3,
  onVisibilityChange,
  onSuggestionsChange,
  onSelectedIndexChange,
  isLoading,
  isFollowUpMode = false
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const { allowedTables, userAccessConfig } = useAuth();
  const [lastSelectedPosition, setLastSelectedPosition] = useState({
    start: -1,
    end: -1,
    text: ''
  });
  const listRef = useRef(null);
  const prevInputRef = useRef(inputValue);
  const prevSuggestionsLengthRef = useRef(0);
  const [isSelectionInProgress, setIsSelectionInProgress] = useState(false);
  const lastSoundPlayedRef = useRef(0);

  // Initialize the useIndexSearch hook
  const { 
    loading, 
    error, 
    debouncedSearch, 
    getCurrentWord 
  } = useIndexSearch({
    minChars,
    maxResults: 10,
    allowedTables,
    cachePrefix: 'autoComplete',
    userAccessConfig
  });

  // Reset lastSelectedPosition when input is cleared or significantly changed
  useEffect(() => {
    const inputCleared = inputValue.length === 0;
    const significantChange = inputValue !== prevInputRef.current &&
                            !inputValue.includes(lastSelectedPosition.text);
    
    if (inputCleared || significantChange) {
      setLastSelectedPosition({ start: -1, end: -1, text: '' });
    }
    
    prevInputRef.current = inputValue;
  }, [inputValue, lastSelectedPosition.text]);

  useEffect(() => {
    if (onVisibilityChange) {
      onVisibilityChange(suggestions.length > 0);
    }
    if (onSuggestionsChange) {
      onSuggestionsChange(suggestions);
    }
    
    // Play notification sound when suggestions first appear with a 3-second cooldown
    if (suggestions.length > 0 && prevSuggestionsLengthRef.current === 0 && !isSelectionInProgress) {
      const currentTime = Date.now();
      if (currentTime - lastSoundPlayedRef.current > 9000) { // 9000ms = 9 seconds
        playNotificationSound();
        lastSoundPlayedRef.current = currentTime;
      }
    }
    
    // Update previous suggestions length for next comparison
    prevSuggestionsLengthRef.current = suggestions.length;
  }, [suggestions, onVisibilityChange, onSuggestionsChange]);
  
  useEffect(() => {
    if (onSelectedIndexChange) {
      onSelectedIndexChange(selectedIndex);
    }
  }, [selectedIndex, onSelectedIndexChange]);

  // Clear suggestions when loading starts
  useEffect(() => {
    if (isLoading) {
      setSuggestions([]);
      setSelectedIndex(-1);
      if (onVisibilityChange) {
        onVisibilityChange(false);
      }
      if (onSuggestionsChange) {
        onSuggestionsChange([]);
      }
      if (onSelectedIndexChange) {
        onSelectedIndexChange(-1);
      }
    }
  }, [isLoading, onVisibilityChange, onSuggestionsChange, onSelectedIndexChange]);

  // Process search results
  const processSearchResults = useCallback(async (wordInfo) => {
    // Don't process search if loading
    if (isLoading) {
      setSuggestions([]);
      return;
    }
    
    const { word, previousWord, combinedWords, start, end } = wordInfo;
    
    // Check if we should search the entire input as a multi-word query
    const fullInput = inputValue.trim();
    const shouldSearchFullInput = fullInput.includes(' ') && fullInput.length >= minChars;
    
    const { results } = await debouncedSearch(word, {
      previousWord,
      currentWord: word,
      combinedWords,
      currentWordStart: start, 
      currentWordEnd: end,
      inputValue,
      lastSelectedPosition,
      // Add full input for multi-word search
      fullInputSearch: shouldSearchFullInput ? fullInput : null
    });
    
    // Double-check loading state before setting suggestions
    if (!isLoading) {
      setSuggestions(results);
      setSelectedIndex(-1); // Reset when input changes
    }
  }, [debouncedSearch, inputValue, lastSelectedPosition, isLoading, minChars]);

  // Handle input changes
  useEffect(() => {
    // Cancel any pending search if loading
    if (isLoading) {
      debouncedSearch.cancel();
      setSuggestions([]);
      return;
    }
    
    const wordInfo = getCurrentWord(inputValue, cursorPosition);
    processSearchResults(wordInfo);
    return () => debouncedSearch.cancel();
  }, [inputValue, cursorPosition, getCurrentWord, processSearchResults, debouncedSearch, isLoading]);

  const handleSelect = useCallback((suggestion) => {
    // Set the selection flag to true at the beginning
    setIsSelectionInProgress(true);
    
    const { word, start, end, hasLPrefix } = getCurrentWord(inputValue, cursorPosition, suggestion.value);
    
    // If original search had 'ל' prefix but the suggestion doesn't, add it back
    const finalValue = hasLPrefix && !suggestion.value.startsWith('ל') 
      ? `ל${suggestion.value}`
      : suggestion.value;

    setLastSelectedPosition({
      start: start,
      end: start + finalValue.length,
      text: finalValue
    });

    // Use the full valueContext array with all occurrences across tables
    const valueContext = suggestion.valueContext || [];

    onSelect({
      ...suggestion,
      suggestion: {
        ...suggestion,
        value: finalValue,
        valueContext // Pass the complete context of all tables containing this value
      },
      wordStart: start,
      wordEnd: end,
      replacementText: finalValue
    });
    
    setSuggestions([]);
    setSelectedIndex(-1); // Reset the selected index back to -1
    
    if (onVisibilityChange) {
      onVisibilityChange(false);
    }
    
    // Reset the flag after a short delay to ensure it covers the next search
    setTimeout(() => {
      setIsSelectionInProgress(false);
    }, 500);
  }, [inputValue, cursorPosition, onSelect, getCurrentWord, onVisibilityChange]);

  const handleKeyDown = useCallback((e) => {
    if (!suggestions.length) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % suggestions.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setSuggestions([]);
        break;
      case 'Backspace':
      case 'Delete':
        setLastSelectedPosition({ start: -1, end: -1, text: '' });
        break;
    }
  }, [suggestions, selectedIndex, handleSelect]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!suggestions.length) return null;

  // Determine positioning class based on input length and mode
  // In follow-up mode, always position on the right
  // In regular mode, position in center if <= 20 chars, otherwise on right
  const positionClass = isFollowUpMode
    ? "absolute w-1/2 right-0 sm:w-64 sm:right-16 sm:top-0 z-50 mt-1"
    : (inputValue.length <= 40
      ? "absolute transform -translate-x-1/2 left-1/2 sm:left-1/2 sm:transform sm:-translate-x-1/2 sm:top-0 z-50 mt-1"
      : "absolute w-1/2 right-0 sm:w-64 sm:right-16 sm:top-0 z-50 mt-1");

  return (
    <div className={positionClass}>
      <div className="w-full sm:w-64 bg-gray-900/80 backdrop-blur-sm border border-gray-700/50 rounded-lg shadow-lg animate-fadeIn">
        <ul 
          className="py-1 overflow-y-auto max-h-60 divide-y divide-gray-700/30"
          ref={listRef}
          role="listbox"
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={`${suggestion.value}`}
              className={`px-3 py-2 cursor-pointer text-sm transition-all duration-150 ${
                index === selectedIndex && selectedIndex !== -1
                ? 'bg-white/10' 
                  : 'hover:bg-white/5'
              }`}
              onClick={() => handleSelect(suggestion)}
              role="option"
              aria-selected={index === selectedIndex}
            >
              <div className={`font-medium ${index === selectedIndex ? 'text-secondary' : 'text-gray-300'}`}>
                {suggestion.value}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">
                {suggestion.columnName.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')} • {suggestion.tableName}
                {suggestion.valueContext && suggestion.valueContext.length > 1 && (
                  <span className="ml-1 text-secondary">+{suggestion.valueContext.length - 1} more</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default React.memo(AutoCompleteSearch);